import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { API, TOKEN_KEY } from "@/lib/api";
import { Logo, LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { PageHeader, MarkdownLite } from "@/components/common";
import { Send, Sparkles, Loader2, Bot, User } from "lucide-react";

const SUGGESTIONS = [
  "What should I focus on today?",
  "Which deals are at risk?",
  "Which leads haven't been contacted?",
  "Show overdue invoices above ₹1 lakh.",
  "Which department is causing the most SLA breaches?",
  "Why might our collections be low?",
];

export default function AIAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
        body: JSON.stringify({ message: msg, session_id: sessionRef.current }),
      });
      sessionRef.current = res.headers.get("X-Session-Id") || sessionRef.current;
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "The AI service is temporarily unavailable. Please try again shortly." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-9rem)] flex-col lg:h-[calc(100vh-7rem)]">
      <PageHeader title={<span className="flex items-center gap-2"><Bot className="h-6 w-6 text-automation" /> AI Assistant</span>}
        subtitle="Grounded in your authorized tenant data · never bypasses permissions" testid="ai-header" />

      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-4 lg:p-6">
          {messages.length === 0 && (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-automation/10 text-automation"><Sparkles className="h-7 w-7" /></div>
              <h3 className="mt-4 font-heading text-lg font-semibold">Ask Blueconnect AI anything</h3>
              <p className="mt-1 max-w-md text-sm text-muted-foreground">I can analyze your leads, deals, invoices and tickets to tell you exactly what needs attention.</p>
              <div className="mt-6 grid w-full max-w-xl gap-2 sm:grid-cols-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} className="rounded-lg border border-border bg-background p-3 text-left text-sm transition-colors hover:border-primary/50 hover:text-primary" data-testid={`ai-suggestion-${s.slice(0, 10)}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-automation/10 text-automation"}`}>
                {m.role === "user" ? <User className="h-4 w-4" /> : <LogoMark className="h-5 w-5" />}
              </span>
              <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-primary text-primary-foreground" : "border border-border bg-background"}`} data-testid={`ai-msg-${m.role}-${i}`}>
                {m.role === "assistant"
                  ? (m.content ? <MarkdownLite text={m.content} /> : (streaming && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin" /> : ""))
                  : m.content}
              </div>
            </motion.div>
          ))}
        </div>

        <div className="border-t border-border p-3 lg:p-4">
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about your leads, deals, invoices…" disabled={streaming} data-testid="ai-input" />
            <Button type="submit" size="icon" className="rounded-full" disabled={streaming || !input.trim()} data-testid="ai-send-btn">
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
}
